import { Edge, Node } from 'reactflow';
import { CUBE_NODE_LAYOUT, estimateCubeNodeHeight, Orientation } from './elkLayout';

type Point = { x: number; y: number };
type CubeAttribute = {
  handleId?: string;
  role?: 'INBOUND' | 'OUTBOUND';
};
type NodeBox = {
  left: number;
  right: number;
  top: number;
  bottom: number;
  centerX: number;
  centerY: number;
};

const EDGE_EXIT_GAP = 28;
const EDGE_DETOUR_GAP = 36;
const EDGE_LANE_GAP = 18;
const EDGE_BLOCKER_PADDING = 16;

function getNodeBox(node: Node): NodeBox {
  const width = CUBE_NODE_LAYOUT.width;
  const height = estimateCubeNodeHeight(node.data);
  const left = node.position.x;
  const top = node.position.y;

  return {
    left,
    right: left + width,
    top,
    bottom: top + height,
    centerX: left + width / 2,
    centerY: top + height / 2,
  };
}

function getHandleCenterY(node: Node, handleId?: string | null, role?: 'INBOUND' | 'OUTBOUND') {
  const baseTop = node.position.y;
  const memberHeight = node.data?.memberName ? CUBE_NODE_LAYOUT.memberSectionHeight : 0;
  const attributes = Array.isArray(node.data?.attributes) ? (node.data.attributes as CubeAttribute[]) : [];
  const inboundAttributes = attributes.filter((attribute) => attribute.role === 'INBOUND');
  const outboundAttributes = attributes.filter((attribute) => attribute.role === 'OUTBOUND');

  let currentY = baseTop + CUBE_NODE_LAYOUT.headerHeight + memberHeight + CUBE_NODE_LAYOUT.dividerHeight;

  if (role === 'INBOUND') {
    currentY += CUBE_NODE_LAYOUT.sectionLabelHeight;
    const attributeIndex = inboundAttributes.findIndex((attribute) => attribute.handleId === handleId);
    if (attributeIndex >= 0) {
      return currentY + attributeIndex * CUBE_NODE_LAYOUT.attributeRowHeight + CUBE_NODE_LAYOUT.attributeRowHeight / 2;
    }
  }

  if (inboundAttributes.length > 0) {
    currentY += CUBE_NODE_LAYOUT.sectionLabelHeight + inboundAttributes.length * CUBE_NODE_LAYOUT.attributeRowHeight;
  }

  if (inboundAttributes.length > 0 && outboundAttributes.length > 0) {
    currentY += CUBE_NODE_LAYOUT.sectionSeparatorHeight;
  }

  if (role === 'OUTBOUND') {
    currentY += CUBE_NODE_LAYOUT.sectionLabelHeight;
    const attributeIndex = outboundAttributes.findIndex((attribute) => attribute.handleId === handleId);
    if (attributeIndex >= 0) {
      return currentY + attributeIndex * CUBE_NODE_LAYOUT.attributeRowHeight + CUBE_NODE_LAYOUT.attributeRowHeight / 2;
    }
  }

  return baseTop + estimateCubeNodeHeight(node.data) / 2;
}

function getEdgeHandleOrder(edge: Edge) {
  return `${edge.sourceHandle || ''}|${edge.targetHandle || ''}|${edge.id}`;
}

function getSiblingOffsets(edges: Edge[]) {
  const groupedEdges = new Map<string, Edge[]>();
  const offsetByEdgeId = new Map<string, number>();

  edges.forEach((edge) => {
    const key = `${edge.source}->${edge.target}`;
    const existing = groupedEdges.get(key) || [];
    existing.push(edge);
    groupedEdges.set(key, existing);
  });

  groupedEdges.forEach((groupEdges) => {
    const sortedEdges = [...groupEdges].sort((leftEdge, rightEdge) =>
      getEdgeHandleOrder(leftEdge).localeCompare(getEdgeHandleOrder(rightEdge))
    );
    const middleIndex = (sortedEdges.length - 1) / 2;

    sortedEdges.forEach((edge, index) => {
      offsetByEdgeId.set(edge.id, (index - middleIndex) * EDGE_LANE_GAP);
    });
  });

  return offsetByEdgeId;
}

function getBlockingNodes(
  edge: Edge,
  nodes: Node[],
  boxByNodeId: Map<string, NodeBox>,
  sourceY: number,
  targetY: number
) {
  const sourceBox = boxByNodeId.get(edge.source);
  const targetBox = boxByNodeId.get(edge.target);

  if (!sourceBox || !targetBox || targetBox.left <= sourceBox.right) {
    return [];
  }

  const corridorTop = Math.min(sourceY, targetY) - EDGE_BLOCKER_PADDING;
  const corridorBottom = Math.max(sourceY, targetY) + EDGE_BLOCKER_PADDING;

  return nodes.filter((node) => {
    if (node.id === edge.source || node.id === edge.target) {
      return false;
    }

    const nodeBox = boxByNodeId.get(node.id);

    if (!nodeBox) {
      return false;
    }

    const intersectsHorizontally = nodeBox.right > sourceBox.right && nodeBox.left < targetBox.left;
    const intersectsVertically = nodeBox.bottom > corridorTop && nodeBox.top < corridorBottom;

    return intersectsHorizontally && intersectsVertically;
  });
}

function buildDirectRoute(sourceX: number, sourceY: number, targetX: number, targetY: number): Point[] {
  const middleX = sourceX + (targetX - sourceX) / 2;

  return [
    { x: sourceX, y: sourceY },
    { x: middleX, y: sourceY },
    { x: middleX, y: targetY },
    { x: targetX, y: targetY },
  ];
}

function buildDetourRoute(
  sourceBox: NodeBox,
  targetBox: NodeBox,
  sourceY: number,
  targetY: number,
  detourY: number
): Point[] {
  const exitX = sourceBox.right + EDGE_EXIT_GAP;
  const entryX = targetBox.left - EDGE_EXIT_GAP;

  return [
    { x: sourceBox.right, y: sourceY },
    { x: exitX, y: sourceY },
    { x: exitX, y: detourY },
    { x: entryX, y: detourY },
    { x: entryX, y: targetY },
    { x: targetBox.left, y: targetY },
  ];
}

function getDetourY(
  blockingNodes: Node[],
  boxByNodeId: Map<string, NodeBox>,
  sourceY: number,
  targetY: number,
  laneOffset: number
) {
  const blockingBoxes = blockingNodes
    .map((node) => boxByNodeId.get(node.id))
    .filter((box): box is NodeBox => Boolean(box));

  const aboveY = Math.min(...blockingBoxes.map((box) => box.top)) - EDGE_DETOUR_GAP;
  const belowY = Math.max(...blockingBoxes.map((box) => box.bottom)) + EDGE_DETOUR_GAP;
  const preferredY = (sourceY + targetY) / 2;

  return Math.abs(preferredY - aboveY) <= Math.abs(preferredY - belowY) ? aboveY + laneOffset : belowY + laneOffset;
}

export function routeDiagramEdges(nodes: Node[], edges: Edge[], orientation: Orientation): Edge[] {
  if (orientation !== 'RIGHT') {
    return edges;
  }

  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const boxByNodeId = new Map(nodes.map((node) => [node.id, getNodeBox(node)]));
  const laneOffsetByEdgeId = getSiblingOffsets(edges);

  return edges.map((edge) => {
    const sourceBox = boxByNodeId.get(edge.source);
    const targetBox = boxByNodeId.get(edge.target);
    const sourceNode = nodeById.get(edge.source);
    const targetNode = nodeById.get(edge.target);

    if (!sourceBox || !targetBox || !sourceNode || !targetNode) {
      return edge;
    }

    const resolvedSourceY = getHandleCenterY(sourceNode, edge.sourceHandle, 'OUTBOUND');
    const resolvedTargetY = getHandleCenterY(targetNode, edge.targetHandle, 'INBOUND');
    const blockingNodes = getBlockingNodes(edge, nodes, boxByNodeId, resolvedSourceY, resolvedTargetY);

    const routePoints =
      blockingNodes.length === 0
        ? buildDirectRoute(sourceBox.right, resolvedSourceY, targetBox.left, resolvedTargetY)
        : buildDetourRoute(
            sourceBox,
            targetBox,
            resolvedSourceY,
            resolvedTargetY,
            getDetourY(
              blockingNodes,
              boxByNodeId,
              resolvedSourceY,
              resolvedTargetY,
              laneOffsetByEdgeId.get(edge.id) || 0
            )
          );

    return {
      ...edge,
      type: 'smoothstep',
      data: {
        ...edge.data,
        routePoints,
        hoverLabel:
          edge.data?.displayName ||
          `${edge.data?.sourceCubeCode || edge.source} -> ${edge.data?.destinationCubeCode || edge.target}`,
        hoverSubLabel: edge.data?.dataFlowType || undefined,
      },
    };
  });
}
